import {Component, OnInit} from '@angular/core';
import {UserDto} from "../../models/admin_owner/user/user-dto";
import {CompanyDto} from "../../models/admin_owner/company/company-dto";
import {ShardHttpService} from "../x-http-requests/shard-http.service";
import {CompanyHttpService} from "../x-http-requests/company-http.service";
import {UserHttpService} from "../x-http-requests/user-http.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ShardNl} from "../../models/admin_owner/shard/shard-nl";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {LoadingComponent} from "../../_modules/x-components/loading/loading.component";
import {AuthenticationService} from "../../_auth/authentication.service";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {AlertLevel} from "../../enums/alert-level";
import {ExternalIdentityInfo, UserAuthenticators} from "../../models/admin_owner/user/external-identity-info";
import {describeAccountActionError} from "../../_shared/errors/account-action-errors";
import {DEMO_COMPANY_LINK_HINT} from "../../_shared/errors/demo-company-errors";
import {InlineNoticeComponent} from "../../_ui/components/feedback/inline-notice/inline-notice.component";
import {
  describeAdministrationError, PASSWORD_CONFIRMATION_MISMATCH
} from "../../_shared/errors/administration-errors";

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    LoadingComponent,
    InlineNoticeComponent,
  ]
})
export class OwnerUserComponent implements OnInit {
  users?: UserDto[];
  filtered_users?: UserDto[];
  companies?: CompanyDto[];
  company?: CompanyDto;
  shard?: ShardNl;
  name: string = "";
  password: string = "";
  cPassword: string = "";
  company_filter: string = "";
  shard_filter: string = "";
  searchText: string = "";
  creator: boolean = false;
  // Toolbar state: status filter + sort key, both applied client-side in applyFilters().
  activeFilter: 'all' | 'active' | 'inactive' = 'all';
  sortKey: 'name' | 'newest' | 'modified' = 'name';
  showFilters: boolean = false; // filter/sort toolbar is collapsed by default

  protected: boolean = false;
  owner: boolean = false;
  admin: boolean = false;

  constructor(private shardHttpService: ShardHttpService,
              private companyHttpService: CompanyHttpService,
              private userHttpService: UserHttpService,
              private authenticationService: AuthenticationService,
              private globalAlertService: GlobalAlertService,
              private route: ActivatedRoute, private router: Router) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  // ----- authenticator management (admin) -----

  authUser?: UserDto;
  authData?: UserAuthenticators;
  allIdentities: ExternalIdentityInfo[] = [];
  selectedIdentityId: string = "";

  /** Shown instead of the identity picker for a user of a demo company (#934). */
  protected readonly demoLinkHint = DEMO_COMPANY_LINK_HINT;

  /**
   * Whether the user being managed belongs to a demo company (#934). Its users are fictional, so the server
   * refuses to link a Microsoft, Google or Apple account to one, and the picker gives way to the reason.
   */
  get AuthUserIsDemo(): boolean {
    return this.authUser?.company?.isDemo === true;
  }

  ManageAuth(user: UserDto): void {
    this.authUser = user;
    this.authData = undefined;
    this.selectedIdentityId = "";
    this.LoadAuth();
    // The directory feeds only the picker, which a demo company's user does not get.
    if (this.AuthUserIsDemo) return;
    this.authenticationService.listAllIdentities().subscribe({
      next: (ids) => (this.allIdentities = ids ?? []),
      error: (err) => console.error(err)
    });
  }

  CloseAuth(): void {
    this.authUser = undefined;
    this.authData = undefined;
  }

  private LoadAuth(): void {
    if (!this.authUser) return;
    this.authenticationService.getUserAuthenticators(this.authUser.id).subscribe({
      next: (data) => (this.authData = data),
      error: (err) => console.error(err)
    });
  }

  LinkIdentity(): void {
    if (!this.authUser || !this.selectedIdentityId || this.AuthUserIsDemo) return;
    this.authenticationService.linkUserIdentity(this.authUser.id, this.selectedIdentityId).subscribe({
      next: () => {
        this.selectedIdentityId = "";
        this.LoadAuth();
      },
      error: (err) => this.ShowAuthError(err)
    });
  }

  RemoveAuth(identityId: string): void {
    if (!this.authUser) return;
    this.authenticationService.removeUserAuthenticator(this.authUser.id, identityId).subscribe({
      next: () => this.LoadAuth(),
      error: (err) => this.ShowAuthError(err)
    });
  }

  TogglePassword(enabled: boolean): void {
    if (!this.authUser || !this.authData) return;
    const previous = this.authData.passwordLoginEnabled;
    // Optimistically reflect the new state in both the modal toggle and the table row
    // (authUser is the same object held in the users list). On failure we revert both, so
    // the checkbox snaps back to its original position and the table stays in sync with
    // the server. The optimistic flip is what makes the [checked] binding re-render on
    // revert — without an actual value change Angular won't rewrite the DOM checkbox.
    this.authData.passwordLoginEnabled = enabled;
    this.authUser.login = enabled;
    this.authenticationService.setUserPasswordLogin(this.authUser.id, enabled).subscribe({
      next: () => this.LoadAuth(),
      error: (err) => {
        if (this.authData) this.authData.passwordLoginEnabled = previous;
        if (this.authUser) this.authUser.login = previous;
        this.ShowAuthError(err);
      }
    });
  }

  /**
   * Reports a failed authenticator action, in **German mapped from the error code**.
   *
   * It used to render `err.error.description`, the server's developer-facing English `ErrorCatalog` text, on a
   * screen whose every label is German (frontend style guide, *"Surfacing an API error"*). The two structured
   * refusals these endpoints can produce are the account-safety rules; everything else they answer — an
   * unknown user, a missing role — is a bare status carrying no `ApiError` at all, and lands on the fallback.
   */
  private ShowAuthError(err: any): void {
    this.globalAlertService.createAndShow("Fehler",
      describeAccountActionError(err, "Die Aktion konnte nicht ausgeführt werden."), AlertLevel.error);
  }

  /** Impersonates the given user (platform owner only). Reloads into the impersonated session. */
  Impersonate(user: UserDto): void {
    const confirmation = confirm("Impersonate the following user?\n" + user.name +
      "\nShard: " + user.shard.name + "\nCompany: " + user.company.name +
      "\n\nThis is logged for auditing.");
    if (!confirmation) return;
    this.authenticationService.startImpersonation(user.id).subscribe({
      next: () => window.open('/', '_self'),
      error: (error) => {
        console.error("Impersonation failed", error);
        // German mapped from `ApiError.code` — never `description`, which is the server's developer-facing
        // English. The realistic refusal here carries no code at all: `[Authorize(Roles = PlatformOwner)]` on
        // ImpersonationController answers a bare 403 before the service can produce one, so the fallback is
        // the sentence most readers of this banner will see.
        const message = describeAccountActionError(error, "Impersonation fehlgeschlagen. Bitte erneut versuchen.");
        this.globalAlertService.createAlertBannerModel("Impersonation nicht möglich", message, AlertLevel.error, 4000);
        this.globalAlertService.show();
      }
    });
  }

  AssignCopy() {
    this.applyFilters();
  }

  FilterItems(value: string) {
    this.searchText = value;
    this.applyFilters();
  }

  setActiveFilter(value: 'all' | 'active' | 'inactive') {
    this.activeFilter = value;
    this.applyFilters();
  }

  setSort(value: 'name' | 'newest' | 'modified') {
    this.sortKey = value;
    this.applyFilters();
  }

  /** Recomputes filtered_users from the search text, active-state filter and sort key. */
  private applyFilters() {
    if (!this.users) {
      this.filtered_users = undefined;
      return;
    }
    const query = this.searchText.trim().toLowerCase();
    let list = this.users.filter(u => !query || u.name.toLowerCase().includes(query));
    if (this.activeFilter === 'active') list = list.filter(u => u.active);
    else if (this.activeFilter === 'inactive') list = list.filter(u => !u.active);
    this.filtered_users = list.sort((a, b) => {
      switch (this.sortKey) {
        case 'newest':
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        case 'modified':
          return new Date(b.modified).getTime() - new Date(a.modified).getTime();
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }

  LoadData() {
    this.userHttpService.list(this.company_filter, this.shard_filter).subscribe(x => {
      this.users = x;
      this.AssignCopy();
    }, error => console.error(error));
    if (this.company_filter != "") {
      this.companyHttpService.get(this.company_filter).subscribe(x => {
        this.companies = [x];
        this.company = x;
        this.SetShard()
      }, error => console.error(error));
    } else {
      this.companyHttpService.list().subscribe(x => {
        this.companies = x.filter(x => x.shards.findIndex(c => c.id == this.shard_filter || this.shard_filter == "") > -1);
        this.companies.forEach(x => {
          x.shards = x.shards.filter(x => x.id == this.shard_filter || this.shard_filter == "");
        })
        if (this.companies.length > 0)
          this.company = this.companies[0];
        this.SetShard();
      }, error => console.error(error));
    }
  }

  AddUser() {
    if (this.cPassword != this.password) {
      this.fail(undefined, PASSWORD_CONFIRMATION_MISMATCH);
      return;
    }
    this.userHttpService.add(this.name, this.password,
      this.shard == undefined ? "" : this.shard.id,
      this.company == undefined ? "" : this.company.id
      , [], [],
      this.protected, this.admin, this.owner).subscribe(x => {
      if (this.users == undefined) this.users = [];
      this.users.splice(0, 0, x);
      this.FilterItems(this.searchText);
      this.name = "";
      this.password = "";
      this.cPassword = "";
    }, error => {
      console.error(error);
      this.fail(error, "Der Benutzer konnte nicht angelegt werden. Bitte prüfen Sie Name und Passwort.");
    });
  }

  RemoveUser(user: UserDto): void {
    let confirmation = confirm("Are you sure you want to delete the following user:\n" + user.name + "\n" + user.id +
      "\nShard: " + user.shard.name + "\nCompany: " + user.company.name);
    if (!confirmation) return;
    this.userHttpService.remove(user.id).subscribe(x => {
      this.users?.splice(this.users?.indexOf(user), 1);
      this.FilterItems(this.searchText);
    }, error => {
      console.error(error);
      // `UserService.Delete` registers no codes: 404, or a bare 400 for a protected/initial account or a
      // self-delete. Naming those beats "try again", which cannot change any of them.
      this.fail(error, "Der Benutzer konnte nicht gelöscht werden. "
        + "Ihr eigenes Konto und geschützte Konten bleiben bestehen.");
    })
  }

  /**
   * Surfaces a user-administration refusal as a German sentence through the global alert banner.
   *
   * Separate from {@link ShowAuthError}, which speaks the *authenticator* vocabulary
   * (`_shared/errors/account-action-errors.ts`) for the identity panel: these are two different endpoints with
   * two different code sets, and one shared mapper would answer each with the other's table. Replaces
   * `ErrorBannerComponent`, which discarded `E_USER_NAME_RESERVED_PREFIX` — the one code the create endpoint
   * here really answers — and showed `Error · Bad Request` instead.
   */
  private fail(err: unknown, fallback: string): void {
    this.globalAlertService.createAndShow(
      "Fehler", describeAdministrationError(err, fallback), AlertLevel.error, 8000);
  }

  SetShard() {
    if (this.company != undefined && this.company.shards.length > 0) this.shard = this.company.shards[0];
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.company_filter = params.company == undefined ? "" : params.company;
      this.shard_filter = params.shard == undefined ? "" : params.shard;
    })
    this.LoadData();
  }

}
